import { useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
// @mui
import {
    Link,
    Stack,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    Alert,
    Divider
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from 'components/iconify';
import LanguagePopover from 'popovers/LanguagePopover';
// server
import { useStateSelector } from '../../context/StateSelectors';
// i18n
import i18n from 'i18';
// utils
import helper from 'utils/helper';
import { formatError, hasError } from 'utils/formatError';
import { LoginWithApple } from '../../sections/auth/LoginWithApple';
import { LoginWithGoogle } from '../../sections/auth/LoginWithGoogle';
import CodeAuthField from '../../components/code-auth-input/CodeAuthField';
import config from '../../config';

// ----------------------------------------------------------------------

export default function LoginForm(props) {
    const navigate = useNavigate();
    const server = useStateSelector((state) => state.server);

    const [showPassword, setShowPassword] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showCodeAuth, setShowCodeAuth] = useState(false);

    // Autofill check https://github.com/facebook/react/issues/1159#issuecomment-1025423604
    const [wasUsernameAutofilled, setWasUsernameAutofilled] = useState(false);
    const [wasPasswordAutofilled, setWasPasswordAutofilled] = useState(false);

    useLayoutEffect(() => {
        const checkAutofilled = () => {
            const usernameAutofilled = !!document
                .getElementById('username')
                ?.matches('*:-webkit-autofill');
            const passwordAutofilled = !!document
                .getElementById('password')
                ?.matches('*:-webkit-autofill');
            setWasUsernameAutofilled(usernameAutofilled);
            setWasPasswordAutofilled(passwordAutofilled);
        };
        // The time when it's ready is not very stable, so check few times
        setTimeout(checkAutofilled, 200);
        setTimeout(checkAutofilled, 500);
        setTimeout(checkAutofilled, 1000);
        setTimeout(checkAutofilled, 2000);
    }, []);

    const handleLoginClick = async () => {
        setError('');
        setLoading(true);

        let result = await server.auth.loginUser(username, password);
        if (result.error === 'OK') {
            setLoading(false);
            if (result.user) {
                navigate('/app/dashboard', { replace: true });
            } else {
                // user uses 2FA, we need to ask for code
                setShowCodeAuth(true);
            }
        } else {
            setLoading(false);
            // setError(formatError(result));
        }
    };

    const handleTwoFactorAuth = async (code) => {
        setError('');
        setLoading(true);
        const result = await server.totp.check(code);
        if (result.error === 'OK') {
            setLoading(false);
            navigate('/app/dashboard', { replace: true });
        } else {
            setLoading(false);
            setError(formatError(result));
        }
    };

    const handleGoogleSuccess = (google) => {
        server.auth
            .loginWithGoogle(google.clientId, google.credential)
            .then((result) => {
                if (!hasError(result)) {
                    if (!result.user) {
                        // user not registered, we need to select role and accept terms to register
                        navigate('/auth/login-by-google', {
                            state: {
                                clientId: google.clientId,
                                credential: google.credential,
                                ...result
                            }
                        });
                    } else {
                        document.location = '/app/dashboard';
                        //navigate('/app', { replace: true });
                    }
                } else {
                    setError(formatError(result));
                }
            });
    };

    // ensuring old session is not used instead of new tokens
    document.cookie = 'JSESSIONID=; expires=Thu, 01 Jan 1970 00:00:01 GMT;';

    return (
        <>
            <Helmet>
                <title>{helper.formPageTitle('auth.login')}</title>
            </Helmet>

            {!showCodeAuth && (
                <>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="h4" gutterBottom>
                            {i18n.t('auth.loginTitle')}
                        </Typography>

                        <LanguagePopover forceSmall={true} />
                    </Stack>

                    <Stack spacing={3} sx={{ mt: 4 }}>
                        <Stack spacing={1}>
                            <Typography variant="body2" color="textSecondary">
                                {i18n.t('columns.email')}
                            </Typography>
                            <TextField
                                id="username"
                                name="username"
                                placeholder={i18n.t('auth.enterEmailAddress')}
                                value={username}
                                onKeyDown={(ev) => {
                                    if (
                                        ev.key === 'Enter' &&
                                        password.length > 0
                                    ) {
                                        handleLoginClick();
                                    }
                                }}
                                onChange={(event) =>
                                    setUsername(event.target.value)
                                }
                                InputLabelProps={{
                                    shrink: false
                                }}
                                variant="outlined"
                            />
                        </Stack>

                        <Stack spacing={1}>
                            <Typography variant="body2" color="textSecondary">
                                {i18n.t('columns.password')}
                            </Typography>
                            <TextField
                                id="password"
                                name="password"
                                placeholder={i18n.t('auth.enterPassword')}
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(event) =>
                                    setPassword(event.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleLoginClick();
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() =>
                                                    setShowPassword(
                                                        !showPassword
                                                    )
                                                }
                                                edge="end"
                                            >
                                                <Iconify
                                                    icon={
                                                        showPassword
                                                            ? 'eva:eye-fill'
                                                            : 'eva:eye-off-fill'
                                                    }
                                                />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                InputLabelProps={{
                                    shrink: false
                                }}
                                variant="outlined"
                            />
                        </Stack>
                    </Stack>

                    <LoadingButton
                        loading={loading}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        sx={{ my: 2, mt: 4 }}
                        onClick={handleLoginClick}
                    >
                        {i18n.t('auth.login')}
                    </LoadingButton>

                    {config.allowGoogleAuth && (
                        <LoginWithGoogle
                            onSuccess={(result) => handleGoogleSuccess(result)}
                        />
                    )}

                    <LoginWithApple />
                </>
            )}

            {showCodeAuth && (
                <Stack spacing={3}>
                    <Typography variant="h5" gutterBottom>
                        {i18n.t('auth.twoFactorAuth')}
                    </Typography>

                    <CodeAuthField callback={handleTwoFactorAuth} />
                </Stack>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {showCodeAuth && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ my: 2, cursor: 'pointer', mt: 4 }}
                >
                    <Link
                        variant="subtitle2"
                        underline="hover"
                        onClick={() => setShowCodeAuth(false)}
                    >
                        {i18n.t('auth.backToLogin')}
                    </Link>
                </Stack>
            )}

            {!showCodeAuth && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ my: 2, cursor: 'pointer', mt: 4 }}
                >
                    {config.allowMerchantRegistration && (
                        <Link
                            variant="subtitle2"
                            underline="hover"
                            onClick={() => navigate('/auth/register')}
                        >
                            {i18n.t('auth.newMerchantRegisterLink')}
                        </Link>
                    )}
                    <Link
                        variant="subtitle2"
                        underline="hover"
                        onClick={() => navigate('/auth/forgot')}
                    >
                        {i18n.t('auth.forgotPasswordLink')}
                    </Link>
                </Stack>
            )}
        </>
    );
}
